/**
 * Глобальный реестр имён приватных ($private) полей всех схем.
 *
 * Заполняется в defineSchema при загрузке модулей (до старта обработки запросов),
 * поэтому к первому RPC-запросу множество уже полное.
 *
 * Назначение — egress-страховка (второй эшелон defense-in-depth). Первый эшелон —
 * default-deny в билдере/репозитории (SELECT/where/orderBy/relation-JSON). Но остаются
 * пути в обход билдера: create/update с returning('*'), сырые this._knex-запросы,
 * доменные методы, возвращающие полную строку. Deep-strip на RPC-выходе выкидывает
 * приватные ключи из любого ответа перед сериализацией клиенту, независимо от пути.
 *
 * Ограничение: strip работает по ИМЕНИ поля. Виртуальная колонка с секретом под
 * другим именем (например, withColumn('x', '(SELECT password_hash ...)')) этим слоем
 * не ловится — такие raw-SQL-каналы закрываются отдельно (RpcService deny-list).
 */
const privateFieldNames = new Set<string>();

/**
 * Имена, слишком общие для приватного поля.
 *
 * Strip работает по имени и не знает, из какой таблицы пришёл объект: приватная
 * колонка `token` вырезала бы ключ `token` и из ответа чужой RPC-функции. Так
 * `identity::generateApiToken` однажды перестал отдавать сгенерированный токен —
 * функция отвечала успехом, а поле по дороге исчезало.
 *
 * Лечится именем: `session_token`, `api_token_hash`, `client_secret` — коллизий
 * не дают и читаются понятнее.
 */
const AMBIGUOUS_PRIVATE_FIELD_NAMES = new Set([
  'token',
  'key',
  'secret',
  'password',
  'hash',
  'code',
  'value',
  'data',
]);

/**
 * Зарегистрировать имена приватных полей (идемпотентно).
 *
 * @param names имена `$private`-полей схемы
 * @param tableName таблица схемы — только для текста ошибки
 * @throws если приватное поле названо слишком общо (см. AMBIGUOUS_PRIVATE_FIELD_NAMES)
 */
export function registerPrivateFieldNames(names: readonly string[], tableName?: string): void {
  for (const name of names) {
    if (AMBIGUOUS_PRIVATE_FIELD_NAMES.has(name)) {
      const where = tableName ? ` (таблица «${tableName}»)` : '';
      throw new Error(
        `Приватное поле «${name}»${where} названо слишком общо. Приватные ключи вырезаются ` +
          `из ЛЮБОГО RPC-ответа по имени, поэтому такое имя погасит одноимённые поля чужих ` +
          `функций и репозиториев. Назовите поле конкретнее, например «${
            tableName ? `${tableName.replace(/s$/, '')}_${name}` : `<сущность>_${name}`
          }».`
      );
    }
    privateFieldNames.add(name);
  }
}

/** Множество всех известных имён приватных полей (для интроспекции/тестов). */
export function getPrivateFieldNames(): ReadonlySet<string> {
  return privateFieldNames;
}

/**
 * Рекурсивно удаляет приватные ключи из значения, мутируя объекты/массивы на месте.
 * Значение — свежий результат RPC перед сериализацией, поэтому мутация безопасна.
 * No-op, если реестр пуст или значение примитивно. Циклы отслеживаются через seen.
 */
export function stripPrivateFieldsDeep<T>(value: T): T {
  if (privateFieldNames.size === 0) return value;
  stripInto(value, new Set<object>());
  return value;
}

function stripInto(value: unknown, seen: Set<object>): void {
  if (value === null || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) stripInto(item, seen);
    return;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (privateFieldNames.has(key)) {
      delete obj[key];
      continue;
    }
    stripInto(obj[key], seen);
  }
}
